import { jsPDF } from "jspdf";

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const num = (v) => Number(v) || 0;

let _logoPromise = null;
const loadLogo = () => {
  if (!_logoPromise) _logoPromise = loadImage("/logo360.png").catch(() => null);
  return _logoPromise;
};
const drawHeaderLogo = (logoImg, W, M, headerH) => {
  if (!logoImg) return null;
  const lh = 56, lw = lh * (logoImg.naturalWidth / logoImg.naturalHeight || 4);
  return { x: (W - lw) / 2, y: M + (headerH - lh) / 2, w: lw, h: lh };
};
const fmt = (n) => (Math.round(n * 100) / 100).toLocaleString("ro-RO");

// Draw an upright name (+ optional kW) label centered on a point.
const drawLabel = (ctx, cx, cy, name, kw) => {
  const two = kw != null;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 13px Arial, sans-serif";
  ctx.lineJoin = "round";
  ctx.lineWidth = 3;
  ctx.strokeStyle = "rgba(0,0,0,0.9)";
  ctx.strokeText(name, cx, cy - (two ? 7 : 0));
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(name, cx, cy - (two ? 7 : 0));
  if (two) {
    ctx.font = "600 10px monospace";
    ctx.strokeText(`${kw} kW`, cx, cy + 7);
    ctx.fillStyle = "#FFD84D";
    ctx.fillText(`${kw} kW`, cx, cy + 7);
  }
};

// Draw the guard-mode glyph (white) centered at (cx,cy): wrench=setup, sun=day, moon=night.
const drawGuardIcon = (ctx, cx, cy, mode, s, bg = "#0EA5E9") => {
  ctx.save();
  ctx.strokeStyle = "#FFFFFF";
  ctx.fillStyle = "#FFFFFF";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (mode === "night") {
    ctx.beginPath();
    ctx.arc(cx, cy, s * 0.85, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.arc(cx + s * 0.5, cy - s * 0.32, s * 0.78, 0, Math.PI * 2);
    ctx.fill();
  } else if (mode === "setup") {
    // wrench: handle + open head ring
    ctx.lineWidth = s * 0.42;
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.55, cy + s * 0.55);
    ctx.lineTo(cx + s * 0.15, cy - s * 0.15);
    ctx.stroke();
    ctx.lineWidth = s * 0.3;
    ctx.beginPath();
    ctx.arc(cx + s * 0.45, cy - s * 0.45, s * 0.42, Math.PI * 0.2, Math.PI * 1.65);
    ctx.stroke();
  } else if (mode === "event") {
    // star (peak/event)
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const rr = i % 2 ? s * 0.45 : s * 1.05;
      const a = -Math.PI / 2 + (i * Math.PI) / 5;
      const px = cx + Math.cos(a) * rr, py = cy + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  } else {
    // sun: disc + rays
    ctx.beginPath();
    ctx.arc(cx, cy, s * 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = s * 0.18;
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * s * 0.78, cy + Math.sin(a) * s * 0.78);
      ctx.lineTo(cx + Math.cos(a) * s * 1.1, cy + Math.sin(a) * s * 1.1);
      ctx.stroke();
    }
  }
  ctx.restore();
};

// Compute the full export layout (sizes, positions, aggregates) shared by the
// canvas (PNG) and vector (PDF) renderers. Loads the background image too.
async function computeExportLayout({
  backgroundUrl, ppm, categories, elements,
  showConsumption = true,
  title = "BIG LITTLE FESTIVAL",
  subtitle = "21–23 August 2026 · Palatul Mogoșoaia, București",
  guardMode = "day",
}) {
  let bgImg = null;
  try { if (backgroundUrl) bgImg = await loadImage(backgroundUrl); } catch { bgImg = null; }

  const M = 40, headerH = 112, legendW = 340, gap = 24;

  const fences = elements.filter((e) => e.kind === "fence" && e.points && e.points.length >= 2);
  const guards = elements.filter((e) => e.kind === "guard");
  const activeGuards = guards.filter((g) => num(g[`agents_${guardMode}`]) > 0);
  const rects = elements.filter((e) => e.kind !== "fence" && e.kind !== "guard");
  const guardModeLabel = { setup: "Set-up", day: "Zi", event: "Event", night: "Noapte" }[guardMode] || "Zi";
  const guardTotals = {
    setup: guards.reduce((s, g) => s + num(g.agents_setup), 0),
    day: guards.reduce((s, g) => s + num(g.agents_day), 0),
    event: guards.reduce((s, g) => s + num(g.agents_event), 0),
    night: guards.reduce((s, g) => s + num(g.agents_night), 0),
  };

  let mapW, mapH, offX, offY;
  if (bgImg) {
    mapW = bgImg.naturalWidth; mapH = bgImg.naturalHeight; offX = 0; offY = 0;
  } else {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    rects.forEach((el) => {
      const w = num(el.length) * ppm, h = num(el.width) * ppm;
      minX = Math.min(minX, el.x); minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + w); maxY = Math.max(maxY, el.y + h);
    });
    fences.forEach((f) => f.points.forEach((pt) => {
      minX = Math.min(minX, pt.x); minY = Math.min(minY, pt.y);
      maxX = Math.max(maxX, pt.x); maxY = Math.max(maxY, pt.y);
    }));
    guards.forEach((g) => {
      minX = Math.min(minX, g.x); minY = Math.min(minY, g.y);
      maxX = Math.max(maxX, g.x); maxY = Math.max(maxY, g.y);
    });
    if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 1200; maxY = 800; }
    const p = 60;
    offX = p - minX; offY = p - minY;
    mapW = maxX - minX + p * 2; mapH = maxY - minY + p * 2;
  }

  const agg = {};
  let total = 0, totalNight = 0;
  rects.forEach((el) => {
    const c = el.category_id;
    if (!agg[c]) agg[c] = { count: 0, consumption: 0, consumption_night: 0 };
    agg[c].count += 1;
    agg[c].consumption += num(el.consumption);
    agg[c].consumption_night += num(el.consumption_night);
    total += num(el.consumption);
    totalNight += num(el.consumption_night);
  });
  const usedCats = categories.filter((c) => agg[c.id]);
  const fenceRows = (fences.some((f) => (f.fence_size || "large") !== "small") ? 1 : 0) +
                    (fences.some((f) => f.fence_size === "small") ? 1 : 0);
  const guardRows = activeGuards.length > 0 ? 1 : 0;
  const legendH = 120 + usedCats.length * 46 + fenceRows * 30 + guardRows * 30 + (guardRows ? 44 : 0);
  const contentH = Math.max(mapH, legendH);

  const W = M + mapW + gap + legendW + M;
  const H = M + headerH + contentH + M;
  const mapX = M, mapY = M + headerH + 16;
  const ex = mapX + offX, ey = mapY + offY;

  return {
    M, headerH, legendW, gap, bgImg,
    fences, guards, activeGuards, rects, guardModeLabel, guardTotals,
    mapW, mapH, offX, offY, agg, usedCats, legendH,
    W, H, mapX, mapY, ex, ey, total, totalNight,
    ppm, categories, elements, showConsumption, title, subtitle, guardMode,
  };
}

// Render the full map (background + elements + labels + legend + totals)
// onto an offscreen canvas and return it.
export async function renderMapCanvas(opts) {
  const L = await computeExportLayout(opts);
  const {
    M, headerH, legendW, gap, bgImg,
    fences, guards, rects, guardModeLabel, guardTotals,
    mapW, mapH, agg, usedCats, activeGuards, legendH,
    W, H, mapX, mapY, ex, ey, total, totalNight,
    ppm, categories, elements, showConsumption, title, subtitle, guardMode,
  } = L;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(W);
  canvas.height = Math.round(H);
  const ctx = canvas.getContext("2d");

  // background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, W, H);

  // header
  ctx.fillStyle = "#09090B";
  ctx.fillRect(M, M, W - M * 2, headerH);
  ctx.fillStyle = "#5E8A1E";
  ctx.fillRect(M, M, 8, headerH);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 30px Arial, sans-serif";
  ctx.fillText(title, M + 28, M + 42);
  // subtitle: event dates + location
  if (subtitle) {
    ctx.fillStyle = "#E4E4E7";
    ctx.font = "500 15px Arial, sans-serif";
    ctx.fillText(subtitle, M + 28, M + 68);
  }
  ctx.fillStyle = "#A1A1AA";
  ctx.font = "400 13px Arial, sans-serif";
  ctx.fillText(
    `${elements.length} elemente · ${new Date().toLocaleDateString("ro-RO")}`,
    M + 28,
    M + 92
  );
  // total consumption (right side of header) — day + night
  ctx.textAlign = "right";
  ctx.fillStyle = "#A1A1AA";
  ctx.font = "400 12px Arial, sans-serif";
  ctx.fillText("Consum electric total", W - M - 24, M + 34);
  ctx.fillStyle = "#FFD84D";
  ctx.font = "700 28px monospace";
  ctx.fillText(`${fmt(total)} kW`, W - M - 24, M + 66);
  ctx.fillStyle = "#93C5FD";
  ctx.font = "700 17px monospace";
  ctx.fillText(`\u263e ${fmt(totalNight)} kW noaptea`, W - M - 24, M + 94);

  // centered 360 Revolution logo in the header
  const _logo = await loadLogo();
  const _lp = drawHeaderLogo(_logo, W, M, headerH);
  if (_lp) ctx.drawImage(_logo, _lp.x, _lp.y, _lp.w, _lp.h);

  // ---- map area ----
  if (bgImg) {
    ctx.drawImage(bgImg, mapX, mapY, mapW, mapH);
  } else {
    ctx.fillStyle = "#F4F4F5";
    ctx.fillRect(mapX, mapY, mapW, mapH);
  }
  ctx.strokeStyle = "#D4D4D8";
  ctx.lineWidth = 1;
  ctx.strokeRect(mapX + 0.5, mapY + 0.5, mapW, mapH);

  // clip elements to the map area
  ctx.save();
  ctx.beginPath();
  ctx.rect(mapX, mapY, mapW, mapH);
  ctx.clip();

  rects.forEach((el) => {
    const cat = categories.find((c) => c.id === el.category_id);
    const color = cat?.color || "#5E8A1E";
    const w = num(el.length) * ppm;
    const h = num(el.width) * ppm;
    const cx = ex + el.x + w / 2;
    const cy = ey + el.y + h / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((num(el.rotation) * Math.PI) / 180);
    ctx.fillStyle = color;
    ctx.fillRect(-w / 2, -h / 2, w, h);
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 2;
    // inset stroke so the outline stays WITHIN the element's real dimensions (not added around it)
    ctx.strokeRect(-w / 2 + 1, -h / 2 + 1, w - 2, h - 2);
    ctx.restore();
  });
  // fences: 4px polylines (large=red, small=orange)
  ctx.lineWidth = 4;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  fences.forEach((f) => {
    ctx.strokeStyle = f.fence_size === "small" ? "#F97316" : "#FF0000";
    ctx.beginPath();
    f.points.forEach((pt, i) => {
      const px = ex + pt.x, py = ey + pt.y;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    });
    ctx.stroke();
  });
  // labels drawn upright (after boxes so they sit on top)
  rects.forEach((el) => {
    const w = num(el.length) * ppm;
    const h = num(el.width) * ppm;
    const cx = ex + el.x + w / 2;
    const cy = ey + el.y + h / 2;
    drawLabel(ctx, cx, cy, el.name || "", showConsumption ? num(el.consumption) : null);
  });
  // fence names (no length details)
  fences.forEach((f) => {
    const cx = ex + f.points.reduce((s, p) => s + p.x, 0) / f.points.length;
    const cy = ey + f.points.reduce((s, p) => s + p.y, 0) / f.points.length;
    drawLabel(ctx, cx, cy, f.name || "", null);
  });
  // guard points: blue shield markers with mode icon + agent count (skip if 0 in this mode)
  guards.forEach((g) => {
    const count = num(g[`agents_${guardMode}`]);
    if (count <= 0) return; // hide guard points with no agents in the current mode
    const gx = ex + g.x, gy = ey + g.y;
    const mw = 30, mh = 32;
    ctx.save();
    ctx.fillStyle = "#0EA5E9";
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 3;
    ctx.beginPath();
    const x0 = gx - mw / 2, y0 = gy - mh / 2, r = 7;
    ctx.moveTo(x0 + r, y0);
    ctx.lineTo(x0 + mw - r, y0);
    ctx.quadraticCurveTo(x0 + mw, y0, x0 + mw, y0 + r);
    ctx.lineTo(x0 + mw, y0 + mh - 12);
    ctx.quadraticCurveTo(x0 + mw, y0 + mh, gx, y0 + mh);
    ctx.quadraticCurveTo(x0, y0 + mh, x0, y0 + mh - 12);
    ctx.lineTo(x0, y0 + r);
    ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // mode icon inside the marker
    drawGuardIcon(ctx, gx, gy - 2, guardMode, 8, "#0EA5E9");
    ctx.restore();
    // agent-count pill below the marker
    ctx.save();
    ctx.font = "700 12px Arial, sans-serif";
    const ptxt = `${count} ag.`;
    const pw = ctx.measureText(ptxt).width + 12;
    const pillY = gy + mh / 2 + 11;
    ctx.fillStyle = "rgba(3,105,161,0.92)";
    ctx.fillRect(gx - pw / 2, pillY - 9, pw, 18);
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(ptxt, gx, pillY);
    ctx.restore();
    // name below the pill
    drawLabel(ctx, gx, pillY + 18, g.name || "", null);
  });
  ctx.restore();

  // ---- legend panel ----
  const lx = mapX + mapW + gap;
  const ly = mapY;
  ctx.fillStyle = "#FAFAFA";
  ctx.fillRect(lx, ly, legendW, legendH);
  ctx.strokeStyle = "#D4D4D8";
  ctx.lineWidth = 1;
  ctx.strokeRect(lx + 0.5, ly + 0.5, legendW, legendH);

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#09090B";
  ctx.font = "700 16px Arial, sans-serif";
  ctx.fillText("Legendă (consum zi / noaptea)", lx + 20, ly + 34);
  ctx.strokeStyle = "#E4E4E7";
  ctx.beginPath();
  ctx.moveTo(lx + 20, ly + 48);
  ctx.lineTo(lx + legendW - 20, ly + 48);
  ctx.stroke();

  let ry = ly + 76;
  usedCats.forEach((c) => {
    const a = agg[c.id];
    // swatch
    ctx.fillStyle = c.color || "#5E8A1E";
    ctx.fillRect(lx + 20, ry - 12, 16, 16);
    ctx.strokeStyle = "#71717A";
    ctx.lineWidth = 1;
    ctx.strokeRect(lx + 20.5, ry - 11.5, 16, 16);
    // name + count
    ctx.fillStyle = "#18181B";
    ctx.font = "500 14px Arial, sans-serif";
    const label = `${c.name} (${a.count})`;
    ctx.fillText(label, lx + 46, ry + 1);
    // day consumption (right)
    ctx.textAlign = "right";
    ctx.fillStyle = "#52525B";
    ctx.font = "600 13px monospace";
    ctx.fillText(`${fmt(a.consumption)} kW`, lx + legendW - 20, ry + 1);
    // night consumption (right, below, blue with moon)
    ctx.fillStyle = "#2563EB";
    ctx.font = "600 12px monospace";
    ctx.fillText(`\u263e ${fmt(a.consumption_night)} kW`, lx + legendW - 20, ry + 20);
    ctx.textAlign = "left";
    ry += 46;
  });

  // fence legend rows (count only, per size)
  const fLarge = fences.filter((f) => (f.fence_size || "large") !== "small");
  const fSmall = fences.filter((f) => f.fence_size === "small");
  const fenceLegend = (color, label) => {
    ctx.fillStyle = color;
    ctx.fillRect(lx + 20, ry - 12, 16, 16);
    ctx.strokeStyle = "#71717A";
    ctx.lineWidth = 1;
    ctx.strokeRect(lx + 20.5, ry - 11.5, 16, 16);
    ctx.fillStyle = "#18181B";
    ctx.font = "500 14px Arial, sans-serif";
    ctx.fillText(label, lx + 46, ry + 1);
    ry += 30;
  };
  if (fLarge.length > 0) fenceLegend("#FF0000", `Garduri mari (${fLarge.length})`);
  if (fSmall.length > 0) fenceLegend("#F97316", `Garduri mici (${fSmall.length})`);

  // guard legend row + agent totals (per mode; exported mode highlighted)
  if (activeGuards.length > 0) {
    ctx.fillStyle = "#0EA5E9";
    ctx.fillRect(lx + 20, ry - 12, 16, 16);
    ctx.strokeStyle = "#71717A";
    ctx.lineWidth = 1;
    ctx.strokeRect(lx + 20.5, ry - 11.5, 16, 16);
    ctx.fillStyle = "#18181B";
    ctx.font = "500 14px Arial, sans-serif";
    ctx.fillText(`Agenți pază (${activeGuards.length} pct.)`, lx + 46, ry + 1);
    ctx.textAlign = "right";
    ctx.fillStyle = "#0369A1";
    ctx.font = "600 13px monospace";
    ctx.fillText(`${guardTotals[guardMode]} agenți · ${guardModeLabel}`, lx + legendW - 20, ry + 1);
    ctx.textAlign = "left";
    ry += 30;
    // per-mode agent totals mini row
    ctx.fillStyle = "#52525B";
    ctx.font = "600 12px monospace";
    ctx.fillText(`Set-up ${guardTotals.setup} · Zi ${guardTotals.day} · Event ${guardTotals.event} · Noapte ${guardTotals.night}`, lx + 46, ry + 1);
    ry += 44;
  }

  // total rows (day + night)
  ry += 6;
  ctx.strokeStyle = "#D4D4D8";
  ctx.beginPath();
  ctx.moveTo(lx + 20, ry - 14);
  ctx.lineTo(lx + legendW - 20, ry - 14);
  ctx.stroke();
  ctx.fillStyle = "#09090B";
  ctx.font = "700 15px Arial, sans-serif";
  ctx.fillText("Total consum (zi)", lx + 20, ry + 6);
  ctx.textAlign = "right";
  ctx.fillStyle = "#5E8A1E";
  ctx.font = "700 15px monospace";
  ctx.fillText(`${fmt(total)} kW`, lx + legendW - 20, ry + 6);
  ctx.textAlign = "left";
  ry += 24;
  ctx.fillStyle = "#1E3A8A";
  ctx.font = "700 15px Arial, sans-serif";
  ctx.fillText("Total noaptea", lx + 20, ry + 6);
  ctx.textAlign = "right";
  ctx.fillStyle = "#2563EB";
  ctx.font = "700 15px monospace";
  ctx.fillText(`\u263e ${fmt(totalNight)} kW`, lx + legendW - 20, ry + 6);
  ctx.textAlign = "left";

  return canvas;
}

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const dateStamp = () => new Date().toISOString().slice(0, 10);

// A3 page in px at a given DPI (297 x 420 mm)
const A3_DPI = 200;
const mmToPx = (mm) => Math.round((mm / 25.4) * A3_DPI);

// Fit the rendered content onto a white A3 page (orientation follows content).
function fitToA3(content) {
  const landscape = content.width >= content.height;
  const pageWmm = landscape ? 420 : 297;
  const pageHmm = landscape ? 297 : 420;
  const pw = mmToPx(pageWmm);
  const ph = mmToPx(pageHmm);
  const page = document.createElement("canvas");
  page.width = pw;
  page.height = ph;
  const ctx = page.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, pw, ph);
  const margin = 0.96;
  const scale = Math.min((pw * margin) / content.width, (ph * margin) / content.height);
  const dw = content.width * scale;
  const dh = content.height * scale;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(content, (pw - dw) / 2, (ph - dh) / 2, dw, dh);
  return { page, landscape, pageWmm, pageHmm };
}

export async function exportMapPNG(opts) {
  const content = await renderMapCanvas(opts);
  const { page } = fitToA3(content);
  await new Promise((resolve) =>
    page.toBlob((blob) => {
      triggerDownload(blob, `harta-A3-${dateStamp()}.png`);
      resolve();
    }, "image/png")
  );
}

export async function exportMapPDF(opts) {
  const L = await computeExportLayout(opts);
  const {
    M, headerH, legendW, gap, bgImg,
    fences, guards, activeGuards, guardModeLabel, guardTotals,
    mapW, mapH, agg, usedCats,
    W, H, mapX, mapY, ex, ey, total, totalNight,
    ppm, categories, elements, showConsumption, guardMode,
    title = "BIG LITTLE FESTIVAL", subtitle = "21–23 August 2026 · Palatul Mogoșoaia, București",
  } = L;

  const landscape = W >= H;
  const pageW = landscape ? 420 : 297;
  const pageH = landscape ? 297 : 420;
  const pdf = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "mm", format: "a3" });

  // px -> mm mapping (fit content centered on the page)
  const k = Math.min((pageW * 0.96) / W, (pageH * 0.96) / H);
  const ox = (pageW - W * k) / 2;
  const oy = (pageH - H * k) / 2;
  const X = (u) => ox + u * k;
  const Y = (v) => oy + v * k;
  const S = (d) => d * k;
  const FS = (f) => f * k * 2.83465; // px height -> pt

  const hex = (h) => {
    h = (h || "#000").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  };
  const setFill = (c) => { const [r, g, b] = hex(c); pdf.setFillColor(r, g, b); };
  const setDraw = (c) => { const [r, g, b] = hex(c); pdf.setDrawColor(r, g, b); };
  const setTxt = (c) => { const [r, g, b] = hex(c); pdf.setTextColor(r, g, b); };
  const setFont = (px, bold = false, mono = false) => {
    pdf.setFont(mono ? "courier" : "helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(FS(px));
  };
  const text = (str, ux, vy, o = {}) => {
    const { px = 12, bold = false, mono = false, color = "#000000", align = "left", baseline = "alphabetic", outline = null } = o;
    setFont(px, bold, mono);
    const opt = { align, baseline };
    if (outline) {
      setDraw(outline.color); pdf.setLineWidth(S(outline.width)); setTxt(color);
      opt.renderingMode = "fillThenStroke";
    } else { setTxt(color); }
    pdf.text(String(str), X(ux), Y(vy), opt);
  };
  const rectPx = (upx, vpx, wpx, hpx, style) => pdf.rect(X(upx), Y(vpx), S(wpx), S(hpx), style);
  const linePx = (x1, y1, x2, y2, color, lwPx) => { setDraw(color); pdf.setLineWidth(S(lwPx)); pdf.line(X(x1), Y(y1), X(x2), Y(y2)); };

  const drawRotRect = (cxpx, cypx, wpx, hpx, angleDeg, fillC) => {
    const a = (angleDeg * Math.PI) / 180, ca = Math.cos(a), sa = Math.sin(a);
    const hw = wpx / 2, hh = hpx / 2;
    const cs = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([dx, dy]) => [cxpx + dx * ca - dy * sa, cypx + dx * sa + dy * ca]);
    const segs = [];
    for (let i = 1; i < 4; i++) segs.push([X(cs[i][0]) - X(cs[i - 1][0]), Y(cs[i][1]) - Y(cs[i - 1][1])]);
    setFill(fillC); setDraw("#FFFFFF"); pdf.setLineWidth(S(2));
    pdf.lines(segs, X(cs[0][0]), Y(cs[0][1]), [1, 1], "FD", true);
  };

  const guardIcon = (uxpx, vypx, mode, spx) => {
    if (mode === "night") {
      setFill("#FFFFFF"); pdf.circle(X(uxpx), Y(vypx), S(spx * 0.85), "F");
      setFill("#0EA5E9"); pdf.circle(X(uxpx + spx * 0.5), Y(vypx - spx * 0.32), S(spx * 0.78), "F");
    } else if (mode === "setup") {
      setDraw("#FFFFFF"); pdf.setLineWidth(S(spx * 0.42));
      pdf.line(X(uxpx - spx * 0.55), Y(vypx + spx * 0.55), X(uxpx + spx * 0.15), Y(vypx - spx * 0.15));
      pdf.setLineWidth(S(spx * 0.3));
      pdf.circle(X(uxpx + spx * 0.45), Y(vypx - spx * 0.45), S(spx * 0.42), "S");
    } else if (mode === "event") {
      setFill("#FFFFFF");
      const pts = [];
      for (let i = 0; i < 10; i++) {
        const rr = i % 2 ? spx * 0.45 : spx * 1.05;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push([uxpx + Math.cos(a) * rr, vypx + Math.sin(a) * rr]);
      }
      const segs = pts.slice(1).map((p, i) => [X(p[0]) - X(pts[i][0]), Y(p[1]) - Y(pts[i][1])]);
      pdf.lines(segs, X(pts[0][0]), Y(pts[0][1]), [1, 1], "F", true);
    } else {
      setFill("#FFFFFF"); pdf.circle(X(uxpx), Y(vypx), S(spx * 0.5), "F");
      setDraw("#FFFFFF"); pdf.setLineWidth(S(spx * 0.18));
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        pdf.line(X(uxpx + Math.cos(a) * spx * 0.78), Y(vypx + Math.sin(a) * spx * 0.78),
          X(uxpx + Math.cos(a) * spx * 1.1), Y(vypx + Math.sin(a) * spx * 1.1));
      }
    }
  };

  const label = (cxpx, cypx, name, kw) => {
    const two = kw != null;
    text(name, cxpx, cypx - (two ? 7 : 0), { px: 13, bold: true, color: "#FFFFFF", align: "center", baseline: "middle", outline: { color: "#000000", width: 1.4 } });
    if (two) text(`${kw} kW`, cxpx, cypx + 7, { px: 10, mono: true, color: "#FFD84D", align: "center", baseline: "middle", outline: { color: "#000000", width: 1.2 } });
  };

  // ---- header ----
  setFill("#09090B"); rectPx(M, M, W - M * 2, headerH, "F");
  setFill("#5E8A1E"); rectPx(M, M, 8, headerH, "F");
  text(title, M + 28, M + 42, { px: 30, bold: true, color: "#FFFFFF" });
  if (subtitle) text(subtitle, M + 28, M + 68, { px: 15, color: "#E4E4E7" });
  text(`${elements.length} elemente · ${new Date().toLocaleDateString("ro-RO")}`, M + 28, M + 92, { px: 13, color: "#A1A1AA" });
  text("Consum electric total", W - M - 24, M + 34, { px: 12, color: "#A1A1AA", align: "right" });
  text(`${fmt(total)} kW`, W - M - 24, M + 66, { px: 28, bold: true, mono: true, color: "#FFD84D", align: "right" });
  text(`${fmt(totalNight)} kW noaptea`, W - M - 24, M + 94, { px: 17, bold: true, mono: true, color: "#93C5FD", align: "right" });

  // centered 360 Revolution logo in the header
  const _logo = await loadLogo();
  const _lp = drawHeaderLogo(_logo, W, M, headerH);
  if (_lp) pdf.addImage(_logo, "PNG", X(_lp.x), Y(_lp.y), S(_lp.w), S(_lp.h));

  // ---- map area (aerial photo stays raster; overlays are vector) ----
  if (bgImg) {
    const c = document.createElement("canvas");
    c.width = bgImg.naturalWidth; c.height = bgImg.naturalHeight;
    c.getContext("2d").drawImage(bgImg, 0, 0);
    pdf.addImage(c.toDataURL("image/jpeg", 0.92), "JPEG", X(mapX), Y(mapY), S(mapW), S(mapH));
  } else {
    setFill("#F4F4F5"); rectPx(mapX, mapY, mapW, mapH, "F");
  }
  setDraw("#D4D4D8"); pdf.setLineWidth(S(1)); rectPx(mapX, mapY, mapW, mapH, "S");

  // rectangles (rotated, vector fill + white outline)
  L.rects.forEach((el) => {
    const cat = categories.find((c) => c.id === el.category_id);
    const w = num(el.length) * ppm, h = num(el.width) * ppm;
    drawRotRect(ex + el.x + w / 2, ey + el.y + h / 2, w, h, num(el.rotation), cat?.color || "#5E8A1E");
  });
  // fences (vector polylines; large=red, small=orange)
  fences.forEach((f) => {
    const pts = f.points.map((p) => [ex + p.x, ey + p.y]);
    const segs = pts.slice(1).map((p, i) => [X(p[0]) - X(pts[i][0]), Y(p[1]) - Y(pts[i][1])]);
    setDraw(f.fence_size === "small" ? "#F97316" : "#FF0000"); pdf.setLineWidth(S(4));
    pdf.lines(segs, X(pts[0][0]), Y(pts[0][1]), [1, 1], "S", false);
  });
  // element labels (name only, white — no per-element consumption on the PDF)
  L.rects.forEach((el) => {
    const w = num(el.length) * ppm, h = num(el.width) * ppm;
    label(ex + el.x + w / 2, ey + el.y + h / 2, el.name || "", null);
  });
  // fence names
  fences.forEach((f) => {
    const cx = ex + f.points.reduce((s, p) => s + p.x, 0) / f.points.length;
    const cy = ey + f.points.reduce((s, p) => s + p.y, 0) / f.points.length;
    label(cx, cy, f.name || "", null);
  });
  // guard markers (skip if 0 agents in current mode)
  guards.forEach((g) => {
    const count = num(g[`agents_${guardMode}`]);
    if (count <= 0) return;
    const gx = ex + g.x, gy = ey + g.y, mw = 30, mh = 32;
    setFill("#0EA5E9"); setDraw("#FFFFFF"); pdf.setLineWidth(S(3));
    pdf.roundedRect(X(gx - mw / 2), Y(gy - mh / 2), S(mw), S(mh), S(7), S(7), "FD");
    guardIcon(gx, gy - 2, guardMode, 8);
    const ptxt = `${count} ag.`;
    const pw = ptxt.length * 7 + 12;
    const pillY = gy + mh / 2 + 11;
    setFill("#0369A1"); rectPx(gx - pw / 2, pillY - 9, pw, 18, "F");
    text(ptxt, gx, pillY, { px: 12, bold: true, color: "#FFFFFF", align: "center", baseline: "middle" });
    label(gx, pillY + 18, g.name || "", null);
  });

  // ---- legend panel ----
  const lx = mapX + mapW + gap, ly = mapY;
  setFill("#FAFAFA"); rectPx(lx, ly, legendW, L.legendH, "F");
  setDraw("#D4D4D8"); pdf.setLineWidth(S(1)); rectPx(lx, ly, legendW, L.legendH, "S");
  text("Legendă (consum zi / noaptea)", lx + 20, ly + 34, { px: 16, bold: true, color: "#09090B" });
  linePx(lx + 20, ly + 48, lx + legendW - 20, ly + 48, "#E4E4E7", 1);

  let ry = ly + 76;
  usedCats.forEach((c) => {
    const a = agg[c.id];
    setFill(c.color || "#5E8A1E"); rectPx(lx + 20, ry - 12, 16, 16, "F");
    setDraw("#71717A"); pdf.setLineWidth(S(1)); rectPx(lx + 20, ry - 12, 16, 16, "S");
    text(`${c.name} (${a.count})`, lx + 46, ry + 1, { px: 14, color: "#18181B" });
    text(`${fmt(a.consumption)} kW`, lx + legendW - 20, ry + 1, { px: 13, mono: true, color: "#52525B", align: "right" });
    text(`${fmt(a.consumption_night)} kW`, lx + legendW - 20, ry + 20, { px: 12, mono: true, color: "#2563EB", align: "right" });
    ry += 46;
  });
  const pdfFLarge = fences.filter((f) => (f.fence_size || "large") !== "small");
  const pdfFSmall = fences.filter((f) => f.fence_size === "small");
  const pdfFenceLegend = (color, lbl) => {
    setFill(color); rectPx(lx + 20, ry - 12, 16, 16, "F");
    setDraw("#71717A"); pdf.setLineWidth(S(1)); rectPx(lx + 20, ry - 12, 16, 16, "S");
    text(lbl, lx + 46, ry + 1, { px: 14, color: "#18181B" });
    ry += 30;
  };
  if (pdfFLarge.length > 0) pdfFenceLegend("#FF0000", `Garduri mari (${pdfFLarge.length})`);
  if (pdfFSmall.length > 0) pdfFenceLegend("#F97316", `Garduri mici (${pdfFSmall.length})`);
  if (activeGuards.length > 0) {
    setFill("#0EA5E9"); rectPx(lx + 20, ry - 12, 16, 16, "F");
    setDraw("#71717A"); pdf.setLineWidth(S(1)); rectPx(lx + 20, ry - 12, 16, 16, "S");
    text(`Agenți pază (${activeGuards.length} pct.)`, lx + 46, ry + 1, { px: 14, color: "#18181B" });
    text(`${guardTotals[guardMode]} agenți · ${guardModeLabel}`, lx + legendW - 20, ry + 1, { px: 13, mono: true, color: "#0369A1", align: "right" });
    ry += 30;
    text(`Set-up ${guardTotals.setup} · Zi ${guardTotals.day} · Event ${guardTotals.event} · Noapte ${guardTotals.night}`, lx + 46, ry + 1, { px: 12, mono: true, color: "#52525B" });
    ry += 44;
  }
  ry += 6;
  linePx(lx + 20, ry - 14, lx + legendW - 20, ry - 14, "#D4D4D8", 1);
  text("Total consum (zi)", lx + 20, ry + 6, { px: 15, bold: true, color: "#09090B" });
  text(`${fmt(total)} kW`, lx + legendW - 20, ry + 6, { px: 15, bold: true, mono: true, color: "#5E8A1E", align: "right" });
  ry += 24;
  text("Total noaptea", lx + 20, ry + 6, { px: 15, bold: true, color: "#1E3A8A" });
  text(`${fmt(totalNight)} kW`, lx + legendW - 20, ry + 6, { px: 15, bold: true, mono: true, color: "#2563EB", align: "right" });

  pdf.save(`harta-A3-${dateStamp()}.pdf`);
}
